import {
  CimpressDocumentUploadOutput,
  CustomerDesignResponse,
  getAspectRatio,
  getClientNewProductOptions,
  getDocumentUploadOutput,
  getStudioConfiguration,
  processUploadClient,
  ValidationManager,
  ValidationState,
  ValidatorType,
} from '@vp/upload-and-print-validator';
import { AnalyticsEventData, sendEventTracking } from 'src/components/Analytics';
import { ProductInformation } from 'src/providers/ProductContextProvider';
import { TrackingConfiguration } from 'src/providers/TrackingContextProvider';
import { getAuthHeader } from 'src/utils/authHeaderUtil';
import { currentCulture } from 'src/utils/cultureUtil';
import { getTenant } from 'src/utils/tenantUtil';
import { TrackingDataForProduct } from 'src/utils/trackingUtil';
import { pollResultUrl } from './UploadAndPrintService';

type ProcessDesignResult = [undefined | string, CustomerDesignResponse];
type DocPressOutput = [string, CimpressDocumentUploadOutput];

enum DesignValidationResult {
  hasProductResolvableErrors = 'hasProductResolvableErrors',
  allValid = 'allValid',
  pageSizeWarningOnly = 'pageSizeWarningOnly',
  blockingErrors = 'blockingErrors',
}

function determineValidationOutcome(validationManager: ValidationManager): DesignValidationResult {
  const validationList = validationManager.getValidationList();

  const allValid = validationList.every(validation => validation.state === ValidationState.Ok);
  if (allValid) return DesignValidationResult.allValid;

  const onlyPageSizeWarning = validationList.every(validation => {
    if (validation.id === ValidatorType.PageSizeValidator) return validation.state === ValidationState.Warning;
    return validation.state === ValidationState.Ok;
  });
  if (onlyPageSizeWarning) return DesignValidationResult.pageSizeWarningOnly;

  const resolvableValidations = validationManager.getResolvableErrorsValidation();
  const stateById = new Map(validationList.map(v => [v.id, v.state]));
  const hasResolvable = resolvableValidations.some(v => stateById.get(v.name) === ValidationState.Error);
  if (hasResolvable) return DesignValidationResult.hasProductResolvableErrors;

  return DesignValidationResult.blockingErrors;
}

function formatDocPrepResponseToCustomerDesignResponse(
  docPrepResponse: CimpressDocumentUploadOutput
): Omit<CustomerDesignResponse, 'validations' | 'documentUploadOutputUrl'> | null {
  if (!docPrepResponse.FilePrepOutput || !docPrepResponse.CimDocUrl) {
    return null;
  }
  const previewUris = docPrepResponse.FilePrepOutput[0].Previews;
  const aspectRatio = getAspectRatio(docPrepResponse);
  return {
    productData: { aspectRatio, price: undefined, productOptions: {} },
    cimDocRefUrl: docPrepResponse.CimDocUrl,
    cimDocUrl: docPrepResponse.CimDocUrl,
    renderingInstructionsUrl: docPrepResponse.RenderingInstructionsUrl,
    previewUris,
  };
}

export async function processAndValidateUploadFromClient(
  validationManager: ValidationManager,
  fileUri: string,
  productInformation: ProductInformation,
  trackingConfiguration: TrackingConfiguration | null,
  trackingDataForProduct: TrackingDataForProduct,
  isVat: boolean,
  setPageSizeCallout: (show: boolean) => void,
  abortSignal?: AbortSignal
): Promise<ProcessDesignResult> {
  const culture = currentCulture.toLowerCase();
  try {
    let validationList = validationManager.getValidationList();
    const validationResult = determineValidationOutcome(validationManager);

    if (validationResult === DesignValidationResult.hasProductResolvableErrors) {
      const newProductOptions = await getClientNewProductOptions(
        {
          validationManager,
          productKey: productInformation.productKey,
          productVersion: productInformation.productVersion,
          quantity: productInformation.quantity,
          culture,
          isVat,
          suggestNewProductOptions: true,
          productOptions: productInformation.selectedOptions,
          pricingContextId: undefined,
        },
        { ...getAuthHeader(), signal: abortSignal }
      );
      return [
        undefined,
        {
          documentUploadOutputUrl: fileUri,
          validations: validationList,
          productData: newProductOptions.productData,
          cimDocRefUrl: undefined,
          cimDocUrl: undefined,
          renderingInstructionsUrl: undefined,
        },
      ];
    }

    if (
      validationResult === DesignValidationResult.allValid ||
      validationResult === DesignValidationResult.pageSizeWarningOnly
    ) {
      const keepAspectRatio = validationResult === DesignValidationResult.allValid;

      if (!keepAspectRatio) {
        // update pageSize validation
        const pageSizeValidationIndex = validationList.findIndex(v => v.id === ValidatorType.PageSizeValidator);
        if (pageSizeValidationIndex !== -1) {
          validationList[pageSizeValidationIndex].state = ValidationState.Ok;
          validationList[pageSizeValidationIndex].message = '';
          // set pagesize callout when there is a fix for pagesize warning
          setPageSizeCallout(true);
        }
      } else {
        setPageSizeCallout(false);
      }

      const [docprepUri, docPrepResponse] = await produceDocPrepResponse(
        { fileUri, productInformation, culture, keepAspectRatio },
        abortSignal
      );

      // Validated if the document has unembedded fonts
      if (docPrepResponse.FilePrepOutput?.[0]?.AreUnembeddedFontsPresent) {
        validationList = validationList.map(validation => {
          if (validation.id === ValidatorType.UnEmbeddedFontsValidator) {
            validation.state = ValidationState.Error;
            validation.message = `validations.${validation.id}.message`;
          }
          return validation;
        });
      }

      const customerFormattedResponse = formatDocPrepResponseToCustomerDesignResponse(docPrepResponse);

      const studioConfigurationReq = await getStudioConfiguration(
        {
          productKey: productInformation.productKey,
          productVersion: productInformation.productVersion,
          selectedOptions: productInformation.selectedOptions,
          tenant: getTenant(),
        },
        { ...getAuthHeader(), signal: abortSignal }
      );

      validationManager.runAdditionalValidations(docPrepResponse, studioConfigurationReq);

      if (customerFormattedResponse) {
        const customerDesignResponse: CustomerDesignResponse = {
          ...customerFormattedResponse,
          validations: validationList,
          documentUploadOutputUrl: fileUri,
        };

        return [docprepUri, customerDesignResponse];
      }
    }

    return [
      undefined,
      {
        documentUploadOutputUrl: fileUri,
        validations: validationList,
        productData: { aspectRatio: 1, price: undefined, productOptions: {} },
        cimDocRefUrl: undefined,
        cimDocUrl: undefined,
        renderingInstructionsUrl: undefined,
      },
    ];
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    const err = e as Error;
    throw new Error(`Error validating design: ${err.message}`);
  } finally {
    sendEventTracking({
      ...trackingConfiguration,
      ...trackingDataForProduct,
      action: 'Document Processing Completed',
      label: 'Client validation processing completed',
      category: 'Design - Upload and Print',
    } as AnalyticsEventData);
  }
}

interface DocPressInput {
  fileUri: string;
  productInformation: ProductInformation;
  culture: string;
  keepAspectRatio: boolean;
}

async function produceDocPrepResponse(
  docPressInput: DocPressInput,
  abortSignal?: AbortSignal
): Promise<DocPressOutput> {
  const { fileUri, productInformation, culture, keepAspectRatio } = docPressInput;
  const uploadResult = await processUploadClient(
    {
      fileUri,
      productKey: productInformation.productKey,
      productVersion: productInformation.productVersion,
      productOptions: productInformation.selectedOptions,
      culture,
      ...(keepAspectRatio ? {} : { keepAspectRatio: false }),
    },
    { ...getAuthHeader(), signal: abortSignal }
  );
  const docPrepUri = await pollResultUrl(uploadResult.resultUrl, abortSignal);
  const docPrepResponse = await getDocumentUploadOutput(docPrepUri, {
    ...getAuthHeader(),
    signal: abortSignal,
  });

  return [docPrepUri, docPrepResponse];
}
